/**
 * Hi! Welcome to the Phaneroo Prayer Requests users store.
 *
 * - All API fetch requests are actually handled inside the `users.service.js` file.
 * - Every action has a corresponding $axios request that performs validation and request handling.
 * - Tests for this store module are located in './tests/users.test.js'
 *
 * @todo
 * - Perform data transforms for dashboard UI
 * - Require collect token in $axios instance from session storage
 *   instead of passing it as param.
 */

import { getUsers, getUserById, createUser, updateUser, deleteUser, updateUserAvatar, updateRole } from '../../services/users.service'
import '../../typedefs'
import assign from 'lodash-es/assign'

export default {
  namespaced: true,
  state: {
    /**
     * @type {Array<User>}
     */
    users: [],
    /**
     * @type {User}
     */
    user: undefined
  },
  getters: {
    /**
     * @returns {User[]}
     */
    users: state => state.users
  },
  mutations: {
    /**
     * Sets specific user to active state
     * @param {Object} state state
     * @param {User} payload New user object
     */
    SET_USER (state, payload) {
      state.user = payload
    },
    /**
     * Sets array of users to active state
     * @param {Object} state state
     * @param {User[]} payload New user object
     */
    SET_USERS (state, payload) {
      state.users = payload
    },
    /**
     * Adds new user to users state
     * @param {Object} state state
     * @param {User} payload New user object
     */
    ADD_USER (state, payload) {
      state.users.unshift(payload)
    },
    /**
     * Updates specific user's object state
     * @param {Object} state state
     * @param {User} payload New user object
     */
    UPDATE_USER (state, payload) {
      const targetIndex = state.users.findIndex(user => user.id === payload.id)
      assign(state.users[targetIndex], payload)
    },
    /**
     * Deletes specific user object from state
     * @param {Object} state state
     * @param {String} id Target user object
     */
    DELETE_USER (state, id) {
      const targetIndex = state.users.findIndex(user => user.id === id)
      state.users.splice(targetIndex, 1)
    }
  },
  actions: {
    /**
     * Vuex context
     * @param {VuexContext} context
     */
    async getUsers ({ commit, rootState }, queryObject) {
      const authToken = rootState.auth.authToken
      const response = await getUsers(authToken, queryObject)
      const users = response.data
      commit('SET_USERS', users)
      return users
    },

    /**
     * Get's a single user by id and adds them to state
     * @param {VuexContext} context Store context
     * @param {String} userId userId
     */
    async findUser ({ commit, rootState }, userId) {
      const authToken = rootState.auth.authToken
      const response = await getUserById(authToken, userId)
      const user = response.data
      commit('ADD_USER', user)
      return user
    },

    /**
     * Get's a single user by id
     * @param {VuexContext} context Store context
     * @param {String} userId userId
     */
    async getUserById ({ commit, rootState }, userId) {
      const authToken = rootState.auth.authToken
      const response = await getUserById(authToken, userId)
      const user = response.data

      commit('SET_USER', user)
      commit('auth/SET_AUTH_USER', {
        user,
        authToken
      }, { root: true })
    },

    /**
     * Creates a new user
     * @param {VuexContext} context
     * @param {User} user
     */
    async createUser ({ commit, rootState }, payload) {
      const authToken = rootState.auth.authToken
      const response = await createUser(authToken, payload)
      const { user } = response.data
      commit('ADD_USER', user)
    },
    /**
     * Updates a user
     * @param {VuexContext} context
     * @param {User} payload
     */
    async updateUser ({ commit, rootState }, payload) {
      const authToken = rootState.auth.authToken
      const response = await updateUser(authToken, payload)
      const { user } = response.data
      commit('UPDATE_USER', user)
    },
    /**
     * Set user role
     * @param {VuexContext} context
     * @param {User} payload
     */
    async updateRole ({ commit, rootState }, payload) {
      const authToken = rootState.auth.authToken
      const response = await updateRole(authToken, payload)
      const { user } = response.data
      commit('UPDATE_USER', user)
    },
    /**
     * Updates a user's avatar
     * @param {VuexContext} context
     * @param {User} payload
     */
    async updateAvatar ({ commit, rootState }, { userId, formData }) {
      const authToken = rootState.auth.authToken
      const response = await updateUserAvatar(authToken, { userId, formData })
      const { user } = response.data
      commit('UPDATE_USER', user)
    },
    /**
     * Deletes a user
     * @param {VuexContext} context
     * @param {User} userId
     */
    async deleteUser ({ commit, rootState }, userId) {
      const authToken = rootState.auth.authToken
      const response = await deleteUser(authToken, userId)
      const { message } = response.data
      commit('DELETE_USER', userId)
      return message
    }
  }
}
